import type { Translate } from '@/entities/moderation';
import {
  describePatchEntry,
  isRemovalOp,
  patchFieldEntries,
  patchImageUrl,
  type PatchEntry,
} from '../../lib/patch-values';
import { PreviewableImage } from './image-preview';

const THUMB_CLASS = 'size-16 rounded-lg object-cover';

/**
 * Readable rendering of a patch-operation field (`image_patches`,
 * `modifier_links`): one line per operation, with a thumbnail for a picture
 * that is being added. Never the raw `{key, value: {op, …}}` JSON.
 */
export function PatchValue({
  entries,
  groupName,
  t,
}: {
  entries: readonly PatchEntry[];
  groupName?: ((id: string) => string | undefined) | undefined;
  t: Translate;
}) {
  return (
    <ul className="flex flex-col gap-2" data-testid="moderation-patch-value">
      {entries.map((entry, index) => {
        const url = isRemovalOp(entry.op) ? '' : patchImageUrl(entry);
        return (
          <li key={`${entry.key}-${index}`} className="flex flex-col gap-1">
            <span>{describePatchEntry(entry, t, groupName)}</span>
            {url !== '' && <PreviewableImage src={url} className={THUMB_CLASS} />}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Modifier changes riding on a product request (`modifier_links`: groups
 * attached / detached), in a block of their own beside the modifier groups —
 * never mixed into the product's own field list — with the request's status
 * so the reviewer sees these changes are awaiting a decision.
 */
export function ModifierChangesBlock({
  value,
  groupName,
  t,
}: {
  value: unknown;
  groupName?: ((id: string) => string | undefined) | undefined;
  t: Translate;
}) {
  const entries = patchFieldEntries('modifier_links', value);
  if (!entries) return null;
  return (
    <div data-testid="moderation-modifier-changes">
      <div className="flex items-center gap-2">
        <h4 className="text-sm font-semibold text-foreground">
          {t('review.patch.modifierChanges', 'Изменения модификаторов')}
        </h4>
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          {t('status.pending', 'На проверке')}
        </span>
      </div>
      <div className="mt-3 text-sm text-destructive">
        <PatchValue entries={entries} groupName={groupName} t={t} />
      </div>
    </div>
  );
}
